import { useEffect, useState } from 'react'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import PlayCircleOutline from '@material-ui/icons/PlayCircleOutline'
import { Formik, Form } from 'formik'
import isEqual from 'lodash/isEqual'
import * as Analytics from 'core/common/analytics'
import { NOT_APPLICABLE_FIELD_VALUE } from 'core/common/constants/datasheet'
import { EVariationTypes } from 'core/common/constants/multivariation'
import { createVariation } from 'core/common/helpers/createVariation'
import i18n from 'core/common/i18n'
import { toLowerStringValues } from 'core/common/utils/formikHelpers'
import { isEmpty } from 'core/common/utils/is'
import { validateField, getErrorMessage } from 'core/common/utils/validation'
import {
  Card,
  CardContent,
  Button,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from 'core/components/Core'
import TextField from 'core/components/TextField'
import ProductRelevanceCallout from 'core/containers/ProductRelevanceCallout'
import ProductVideo from 'core/containers/ProductUpload/components/ProductVideo'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { useMultivariationValues } from '../MultiVariation/store'
import ProductDialogEanTips from './components/ProductDialogEanTips'
import ProductDialogVariationTips from './components/ProductDialogVariationTips'
import UploadImageField from './components/UploadImageField'
import VariationsAccordion from './components/VariationsAccordion'
import VariationSettingsDialog from './components/VariationSettingsDialog'
import {
  groupByMainVariation,
  createAllAttributesCombinations,
  extractCustomAttributeTypes,
  generateVariation,
} from './helper'
import { SimpleProductDetailsSchema, VariationProductDetailsSchema } from './schema'
import * as S from './style'

export const ProductVariationDetailsForm = ({
  custom,
  onNext,
  isEditMode,
  onEditSubmit,
  navigateToStep,
  handleEditStep,
  step,
  formMode,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isVariationTipsOpen, setOpenVariationTips] = useState(false)
  const [isEanTipsOpen, setOpenEanTips] = useState(false)
  const [isVariationSettingsOpen, setOpenVariationSettings] = useState(false)
  const [variationsGrouped, setVariationsGrouped] = useState({})
  const [isAddingMainAttribute, setIsAddingMainAttribute] = useState(false)
  const [isAddingOtherAttributes, setIsAddingOtherAttributes] = useState(false)
  const [isURLImageActive, setIsURLImageActive] = useState(false)
  const { variationTypes, variationValues, setVariationType } = useMultivariationValues()
  const [expanded, setExpanded] = useState([])

  const { formData, setFormData } = useFormContext()
  const [progressState, setProgressState] = useState({})
  const hasVariations = !isEmpty(variationsGrouped)
  const customAttributesTypes = extractCustomAttributeTypes(formData.variations)

  const initialValues = {
    sku: formData.sku,
    ean: formData.not_has_ean ? NOT_APPLICABLE_FIELD_VALUE : formData.ean,
    not_has_ean: Boolean(formData.not_has_ean),
    uploadedImages: formData.uploadedImages,
    video: formData.video,
    searchVideo: formData.searchVideo,
    variations: formData.variations.map(variation => ({
      ...variation,
      ean: variation.not_has_ean ? NOT_APPLICABLE_FIELD_VALUE : variation.ean,
      not_has_ean: Boolean(variation.not_has_ean),
    })),
  }

  useEffect(() => {
    if (isEditMode) return

    const allAttributesCombinations = createAllAttributesCombinations(
      variationTypes,
      variationValues
    )

    const createAllVariations = allAttributesCombinations => {
      const allVariations = allAttributesCombinations.map(variationAttributes => {
        const variation = formData.variations.find(findableVariation => {
          return isEqual(
            toLowerStringValues(findableVariation.customAttributes),
            toLowerStringValues(variationAttributes)
          )
        })

        if (variation) {
          return {
            ...variation,
          }
        }

        return createVariation({ customAttributes: variationAttributes })
      })

      const variations = allVariations.length ? allVariations : formData.variations

      setFormData(prevData => ({
        ...prevData,
        variations,
      }))

      return variations
    }

    const allVariations = createAllVariations(allAttributesCombinations)

    const grouped = groupByMainVariation(allVariations)
    setVariationsGrouped(grouped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variationTypes, variationValues, onNext, navigateToStep, onEditSubmit])

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsLoading(true)
        setIsSubmitting(false)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  useEffect(() => {
    const grouped = groupByMainVariation(formData.variations)
    setVariationsGrouped(prevGroup => ({ ...prevGroup, ...grouped }))
  }, [formData.variations])

  const saveDataForm = values => {
    setFormData(prevData => ({
      ...prevData,
      video: values.video,
      searchVideo: values.searchVideo,
      sku: values.sku.trim(),
      ean: values.not_has_ean ? '' : values.ean.trim(),
      not_has_ean: values.not_has_ean,
      uploadedImages: values.uploadedImages,
      variations: values.variations?.map(variation => ({
        ...variation,
        ean: variation?.not_has_ean ? '' : variation.ean.trim(),
        sku: variation.sku.trim(),
      })),
    }))
  }

  const onSubmit = (values, { skipSettings }) => {
    if (formMode === 'com-variacoes' || formMode === 'editar') {
      const EVariationTypesValids = Object.values(EVariationTypes).map(type => type.toLowerCase())

      const variationsTypesByMode = isEditMode ? customAttributesTypes : variationTypes

      const translatedAttributesTypes = Object.values(variationsTypesByMode || {})
        ?.filter(attrType => EVariationTypesValids.includes(attrType))
        ?.map(attrType => i18n.t(`multivariation.types.${attrType}`))
        .join(', ')

      Analytics.ProductVariationAttributesListSaved(formMode, translatedAttributesTypes)
    }

    const hasEan =
      (values?.ean && values?.ean !== NOT_APPLICABLE_FIELD_VALUE) ||
      values?.variations?.some(
        variation => variation.ean && variation.ean !== NOT_APPLICABLE_FIELD_VALUE
      )

    if (hasEan) {
      Analytics.ProductWithEANSaved(formMode)
    } else {
      Analytics.ProductWithoutEANSaved(formMode)
    }

    if (!skipSettings && values.variations.find(variation => variation.newVariation)) {
      return setOpenVariationSettings(true)
    }

    saveDataForm(values)
    setIsSubmitting(true)
    setExpanded([])

    if (onNext) {
      onNext()
    }
  }

  const handleVariationsValidation = async formik => {
    if (hasVariations) {
      const errors = await formik.validateForm()
      const { variations } = formik.values

      if (errors.variations && Array.isArray(errors.variations)) {
        const variationsIds = variations
          .map((variation, index) => (errors.variations[index] ? variation.id : null))
          .filter(Boolean)

        const errorPanels = Array.from(
          new Set(
            variationsIds.flatMap(targetId =>
              Object.entries(variationsGrouped)
                .map(([key, array]) => {
                  const found = array.some(item => item.id === targetId)
                  return found ? key : null
                })
                .filter(key => key !== null)
            )
          )
        )

        setExpanded(prev => Array.from(new Set([...prev, ...errorPanels])))
      }
    }
  }

  const handleAccordionChange = (panel, values) => (event, isExpanded) => {
    saveDataForm(values)
    setExpanded(prev => (isExpanded ? [...prev, panel] : prev.filter(item => item !== panel)))
  }

  const onAddMainAttribute = values => {
    const newVariations = []
    const notHasSecondAttribute = !customAttributesTypes[1]

    values.firstAttribute.forEach(({ title }) => {
      if (notHasSecondAttribute) {
        newVariations.push(
          generateVariation({
            values,
            variationGroupName: title,
            variationGroupList: [],
            customAttributesTypes,
          })
        )

        return
      }

      setVariationsGrouped(prevGroup => {
        if (!(title in prevGroup)) {
          return { ...prevGroup, [title]: [] }
        }

        return prevGroup
      })
    })

    if (newVariations.length) {
      saveNewVariations({ values, newVariations: [...newVariations] })
    }

    setIsAddingMainAttribute(false)
  }

  const saveNewVariations = ({ values, newVariations }) => {
    setFormData(prevData => ({
      ...prevData,
      variations: [...values.variations, ...newVariations],
    }))
  }

  const updateImages = ({ variationGroupList, setFieldValue, files, values }) => {
    if (!hasVariations) {
      return setFieldValue('uploadedImages', files)
    }

    const updatedVariations = [...values.variations]

    variationGroupList.forEach(variationItem => {
      const variationIndex = values.variations.findIndex(item => item.id === variationItem.id)

      if (variationIndex !== -1) {
        updatedVariations[variationIndex] = {
          ...updatedVariations[variationIndex],
          images: files,
        }
      }
    })

    setFieldValue('variations', updatedVariations)
    saveDataForm({ ...values, variations: updatedVariations })
  }

  const copyGroupVariationValues = ({ values, variationsGrouped }) => {
    const variationsGroupUpdated = Object.values(variationsGrouped).map(group => {
      const firstVariationOfGroup = group[0]

      if (firstVariationOfGroup) {
        return group.map(variation => {
          if (variation.newVariation) {
            const findableVariation = values.variations.find(v => v.id === variation.id)

            return {
              ...findableVariation,
              package: firstVariationOfGroup.package ?? variation.package,
              prices: firstVariationOfGroup.prices ?? variation.prices,
              stocks: firstVariationOfGroup.stocks ?? variation.stocks,
            }
          }

          return variation
        })
      }

      return group
    })

    const variationsUpdated = variationsGroupUpdated.flatMap(group => group)

    onSubmit({ ...values, variations: variationsUpdated }, { skipSettings: true })
    setOpenVariationSettings(false)
  }

  const renderProductFields = ({
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    setFieldValue,
  }) => {
    return (
      <div className="d-flex">
        <div className="mr-3 flex-1">
          <UploadImageField
            formData={formData}
            hasVariations={hasVariations}
            formMode={formMode}
            custom={custom}
            setProgressState={setProgressState}
            isURLImageActive={isURLImageActive}
            handleEditStep={handleEditStep}
            updateImages={updateImages}
            step={step}
            setIsURLImageActive={setIsURLImageActive}
          />
        </div>

        <div style={{ flex: 0.6 }}>
          <div className="d-flex flex-column">
            <div className="d-flex mb-3">
              <TextField
                fullWidth
                name="sku"
                variant="outlined"
                label="SKU"
                value={values?.sku}
                onChange={e => {
                  handleChange(e)
                  handleEditStep(step.key)
                }}
                onBlur={handleBlur}
                style={{ backgroundColor: '#fff' }}
                error={validateField('sku', errors, touched, submitCount > 0)}
                helperText={getErrorMessage('sku', errors, touched)}
                disabled={isEditMode}
              />

              <HelpOutlineIcon
                style={{ marginLeft: 4, fontSize: 16 }}
                color="primary"
                onClick={() => setOpenEanTips(true)}
                className="cursor-pointer"
              />
            </div>
          </div>

          <div className="d-flex flex-column flex-1">
            <div className="d-flex mb-3">
              <TextField
                fullWidth
                name="ean"
                variant="outlined"
                label="EAN"
                value={values?.ean}
                onChange={e => {
                  handleChange(e)
                  handleEditStep(step.key)
                }}
                onBlur={handleBlur}
                style={{ backgroundColor: '#fff' }}
                error={validateField('ean', errors, touched, submitCount > 0)}
                helperText={getErrorMessage('ean', errors, touched)}
                InputLabelProps={{
                  shrink: Boolean(values?.ean),
                }}
                disabled={values?.not_has_ean}
              />
              <HelpOutlineIcon
                style={{ marginLeft: 4, fontSize: 16 }}
                color="primary"
                onClick={() => setOpenEanTips(true)}
                className="cursor-pointer"
              />
            </div>
            <div>
              <FormControlLabel
                control={<Checkbox defaultChecked={values?.not_has_ean} />}
                label={i18n.t('productNew.regulatoryInformation.notApply')}
                onChange={(event, checked) => {
                  if (checked) {
                    setFieldValue('not_has_ean', true)
                    setFieldValue('ean', NOT_APPLICABLE_FIELD_VALUE)

                    handleEditStep(step.key)
                    return
                  }

                  setFieldValue('ean', '')
                  setFieldValue('not_has_ean', false)

                  handleEditStep(step.key)
                }}
                value={values.not_has_ean}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Formik
      key={variationsGrouped}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={hasVariations ? VariationProductDetailsSchema : SimpleProductDetailsSchema}
    >
      {formikProps => {
        return (
          <Form className="w-100">
            <Card raised elevation={1} className="p-4">
              <CardContent className="p-0">
                <Typography variant="h5" component="h2" className="mb-4">
                  {!isEditMode && i18n.t('productNew.variationDetails.title')}
                </Typography>

                <Typography variant="body1" color="secondary" className="mb-4">
                  {i18n.t('productNew.variationDetails.subtitle')}{' '}
                  {custom?.variationsTipLink ? (
                    <Link
                      variant="button"
                      target="_blank"
                      href={custom?.variationsTipLink}
                      className="cursor-pointer"
                    >
                      {i18n.t('productNew.variationDetails.tipsLink')}
                    </Link>
                  ) : (
                    <Link
                      variant="button"
                      onClick={() => setOpenVariationTips(true)}
                      className="cursor-pointer"
                    >
                      {i18n.t('productNew.variationDetails.tipsLink')}
                    </Link>
                  )}
                </Typography>

                <ProductRelevanceCallout
                  disableScoreInfo={custom ? custom?.disableScoreInfo : false}
                  recommendations={[
                    i18n.t('productNew.variationDetails.relevanceRecommendation1'),
                    custom ? custom?.recomendation : null,
                    i18n.t('productNew.variationDetails.relevanceRecommendation2'),
                  ]}
                  onClick={() => Analytics.ProductVariationsRelevanceClicked(formMode)}
                />

                {hasVariations && (
                  <VariationsAccordion
                    variationsGrouped={variationsGrouped}
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    handleEditStep={handleEditStep}
                    isEditMode={isEditMode}
                    setOpenEanTips={setOpenEanTips}
                    customAttributesTypes={customAttributesTypes}
                    isAddingOtherAttributes={isAddingOtherAttributes}
                    setIsAddingOtherAttributes={setIsAddingOtherAttributes}
                    setVariationType={setVariationType}
                    hasVariations={hasVariations}
                    step={step}
                    formData={formData}
                    formMode={formMode}
                    saveNewVariations={saveNewVariations}
                    isAddingMainAttribute={isAddingMainAttribute}
                    onAddMainAttribute={onAddMainAttribute}
                    setIsAddingMainAttribute={setIsAddingMainAttribute}
                    variationTypes={variationTypes}
                    custom={custom}
                    setProgressState={setProgressState}
                    isURLImageActive={isURLImageActive}
                    updateImages={updateImages}
                    setIsURLImageActive={setIsURLImageActive}
                  />
                )}

                {!hasVariations && renderProductFields(formikProps)}

                <S.VideoContainer className="d-flex mt-12">
                  <S.VideoIconBox className="d-flex align-center px-7">
                    <PlayCircleOutline fontSize="inherit" color="inherit" />
                  </S.VideoIconBox>

                  <div className="mb-3">
                    <ProductVideo
                      noDeleteDialog
                      onVideoLoad={() => {
                        Analytics.ProductVideoUploadSaved(formMode)
                      }}
                      onVideoError={() => {
                        Analytics.ProductVideoUploadError(formMode)
                      }}
                    />
                  </div>
                </S.VideoContainer>

                <div className="w-100 d-flex justify-end mt-8">
                  {!isEditMode && (
                    <>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          formikProps.resetForm()
                          handleEditStep(null)
                        }}
                        className="mr-2"
                      >
                        <span>{i18n.t('productNew.actions.cancel')}</span>
                      </Button>

                      <Button
                        variant="contained"
                        color="primary"
                        disabled={progressState.UPLOAD_STATUS === 'IN_PROGRESS'}
                        onClick={() => {
                          formikProps.handleSubmit()
                          handleVariationsValidation(formikProps)
                        }}
                      >
                        {i18n.t('productNew.actions.continue')}
                      </Button>
                    </>
                  )}

                  {isEditMode && (
                    <>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => formikProps.resetForm()}
                        className="mr-2"
                      >
                        <span>{i18n.t('productNew.actions.cancel')}</span>
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          formikProps.handleSubmit()
                          handleVariationsValidation(formikProps)
                        }}
                        disabled={isSubmitting}
                        startIcon={
                          isSubmitting ? <CircularProgress color="inherit" size={16} /> : null
                        }
                        loading={isLoading}
                      >
                        <span>{i18n.t('productNew.actions.save')}</span>
                      </Button>
                    </>
                  )}
                </div>

                <ProductDialogVariationTips
                  open={isVariationTipsOpen}
                  onClose={() => setOpenVariationTips(false)}
                />

                <ProductDialogEanTips open={isEanTipsOpen} onClose={() => setOpenEanTips(false)} />

                <VariationSettingsDialog
                  open={isVariationSettingsOpen}
                  onClose={() => setOpenVariationSettings(false)}
                  handleCopy={() =>
                    copyGroupVariationValues({
                      values: formikProps.values,
                      variationsGrouped,
                    })
                  }
                  handleManual={() => {
                    setFormData(prevData => ({
                      ...prevData,
                      pendingFieldsVariationFlow: true,
                    }))
                    navigateToStep('shipping-details-1')
                  }}
                />
              </CardContent>
            </Card>
          </Form>
        )
      }}
    </Formik>
  )
}
